/* eslint-disable babel/camelcase */
import React from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { InProgressAlert } from '~/components/SearchResults';
import {
    ClientContext,
    SearchContext,
    SearchContextType,
    ServicesContext,
    useSearchClinic,
    useSearchPCP,
} from '~/services';
import {
    enumValue,
    formatNumber,
    hasUnsavedFilters,
    prepareSearchQueryString,
    rollUpStatStatuses,
    useLocationFilters,
    useServiceSubscribe,
} from '~/util';

interface GapStatsType {
    status: string | number;
    count: string | number;
    name?: string;
}
type GapSectionType = 'risk_gaps' | 'foc_gaps' | 'quality_gaps';
export type PatientStatsType = {
    [key in GapSectionType]: GapStatsType[];
};
interface GapAggregateTotal {
    ready_with_answer: number;
    oldest_answer_at: null | string;
}
export interface PatientAggregateStatsType extends PatientStatsType {
    patients: { count: number };
    gap_totals: {
        risk_gap: GapAggregateTotal;
        foc_gap: GapAggregateTotal;
        quality_gap: GapAggregateTotal;
        ready_with_answer?: number;
    };
}

interface CategoryItemType {
    label: string;
    class: string;
    value: number | string;
    title?: string;
}
interface CategoriesType {
    queryString: null | string;
    reportYear: null | number;
    members: { members: CategoryItemType };
    healthhx: { healthhx: CategoryItemType };
    gaps: { in_progress: CategoryItemType };
    risk_gaps: {
        [key: number]: CategoryItemType;
        percent: CategoryItemType;
    };
    foc_gaps: {
        [key: number]: CategoryItemType;
    };
    quality_gaps: {
        [key: number]: CategoryItemType;
    };
}
export type StatisticSectionKey = keyof CategoriesType;
export type StatisticItemKey = string | number;

export interface ParentStatisticsProps {
    evt: null | string;
    patientView: boolean;
    getLink: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        section: StatisticSectionKey,
        item: StatisticItemKey
    ) => void;
    getActiveBadge: (section: StatisticSectionKey, item: StatisticItemKey) => string;
    patientId?: null | number;
    isResultsPage?: boolean;
    healthHxVal?: string | number;
    scrollToItem?: (item: string) => void;
}
export function ParentStatistics({
    evt,
    patientView,
    getLink,
    getActiveBadge,
    patientId = null,
    isResultsPage = false,
    healthHxVal = '0',
    scrollToItem = () => {},
}: ParentStatisticsProps) {
    const servicesContext = React.useContext(ServicesContext);
    const { reportYear } = React.useContext(ClientContext);
    const { clinic } = useSearchClinic();
    const { PCP } = useSearchPCP();
    const { searchValues } = React.useContext(SearchContext) as SearchContextType;
    const [loading, setLoading] = React.useState(false);
    const [categories, setCategories] = React.useState<CategoriesType>({
        queryString: null,
        reportYear: null,
        members: {
            members: {
                label: 'Total',
                class: 'light',
                value: 0,
                title: 'Number of members based on current search criteria',
            },
        },
        healthhx: {
            healthhx: {
                label: 'Health Hx',
                class: 'light',
                value: 0,
                title: 'Available patient health history information',
            },
        },
        gaps: {
            in_progress: {
                label: 'In Progress',
                class: 'purple',
                value: 0,
                title: 'Responses saved but not submitted',
            },
        },
        risk_gaps: {
            [enumValue(['gap', 'status', 'Pending']) as number]: {
                label: 'Pending',
                class: 'info',
                value: 0,
                title: 'Number of pending ICDs',
            },
            [enumValue(['gap', 'status', 'Fail']) as number]: {
                label: 'Failed',
                class: 'warning',
                value: 0,
                title: 'Number of returned ICDs due to Failed Validation',
            },
            [enumValue(['gap', 'status', 'Under Review']) as number]: {
                label: 'Review',
                class: 'secondary',
                value: 0,
                title: 'Number of submitted ICDs currently under review',
            },
            [enumValue(['gap', 'status', 'Addressed']) as number]: {
                label: 'Addr',
                class: 'success',
                value: 0,
                title: 'Number of addressed ICDs',
            },
            percent: { label: 'Addr %', class: 'light', value: '0%' },
        },
        foc_gaps: {
            [enumValue(['gap', 'status', 'Pending']) as number]: {
                label: 'Pending',
                class: 'info',
                value: 0,
                title: 'Number of open gaps',
            },
            [enumValue(['gap', 'status', 'Under Review']) as number]: {
                label: 'Review',
                class: 'secondary',
                value: 0,
                title: 'Number of submitted gaps currently under review',
            },
            [enumValue(['gap', 'status', 'Addressed']) as number]: {
                label: 'Addr',
                class: 'success',
                value: 0,
                title: 'Number of addressed gaps',
            },
        },
        quality_gaps: {
            [enumValue(['gap', 'status', 'Pending']) as number]: {
                label: 'Pending',
                class: 'info',
                value: 0,
                title: 'Number of open gaps',
            },
            [enumValue(['gap', 'status', 'Fail']) as number]: {
                label: 'Failed',
                class: 'warning',
                value: 0,
                title: 'Number of returned gaps due to Failed Validation',
            },
            [enumValue(['gap', 'status', 'Under Review']) as number]: {
                label: 'Review',
                class: 'secondary',
                value: 0,
                title: 'Number of submitted gaps currently under review',
            },
            [enumValue(['gap', 'status', 'Addressed']) as number]: {
                label: 'Addr',
                class: 'success',
                value: 0,
                title: 'Number of addressed gaps',
            },
        },
    });
    const [oldestInProgress, setOldestInProgress] = React.useState<null | string>(null);
    const filters = useLocationFilters();
    const categoryCache = React.useRef<{ [key: number]: CategoriesType }>({});
    // only enable cache on dashboard page
    const cacheEnabled = !isResultsPage && !patientView;

    const buildStats = React.useCallback(
        (
            prev: typeof categories,
            result: PatientStatsType | PatientAggregateStatsType,
            section: GapSectionType
        ) => {
            result[section].forEach((x) => {
                if (prev[section][x.status as number]) {
                    prev[section][x.status as number].value = x.count as number;
                }
            });
            return { ...prev, [section]: prev[section] };
        },
        []
    );

    const sortOldestSubmissionAnswers = React.useCallback(
        (result: PatientAggregateStatsType) => {
            const itemsToSort = [
                result.gap_totals.risk_gap.oldest_answer_at,
                result.gap_totals.quality_gap.oldest_answer_at,
            ];
            if (!servicesContext.user.hasFeatureToggle('hidden_foc')) {
                itemsToSort.push(result.gap_totals.foc_gap.oldest_answer_at);
            }
            return itemsToSort.sort()[0];
        },
        [servicesContext.user]
    );

    const loadStats = React.useCallback(async () => {
        let patientResult: undefined | PatientStatsType;
        let aggregateResult: undefined | PatientAggregateStatsType;

        if (patientView) {
            patientResult = servicesContext.patient.stats(patientId);
        } else {
            let queryString: string;
            if (isResultsPage) {
                // If the searchValues haven't yet been updated to match the query filters,
                // wait for SearchResults to do the synchronization
                if (hasUnsavedFilters(filters, searchValues)) {
                    return;
                }
                queryString = prepareSearchQueryString(
                    {
                        ...searchValues,
                        clinic,
                        PCP,
                    },
                    true
                );
            } else {
                queryString = prepareSearchQueryString({
                    clinic,
                    PCP,
                    disenrolled: searchValues.disenrolled,
                });
            }
            setLoading(true);
            const res = await servicesContext.api.call({
                method: 'get',
                url: `/api/gap-aggregates?${queryString}`,
            });
            aggregateResult = rollUpStatStatuses(
                res,
                !servicesContext.user.hasFeatureToggle('hidden_foc')
            );
            setCategories((prev) => ({
                ...prev,
                queryString,
                reportYear,
                members: {
                    members: {
                        ...prev.members.members,
                        value: aggregateResult!.patients.count,
                    },
                },
            }));
            setCategories((prev) => ({
                ...prev,
                gaps: {
                    in_progress: {
                        ...prev.gaps.in_progress,
                        value: aggregateResult!.gap_totals.ready_with_answer as number,
                    },
                },
            }));
            setOldestInProgress(sortOldestSubmissionAnswers(aggregateResult));
        }
        const result = patientResult || aggregateResult;
        setCategories((prev) => buildStats(prev, result!, 'risk_gaps'));
        setCategories((prev) => buildStats(prev, result!, 'foc_gaps'));
        setCategories((prev) => buildStats(prev, result!, 'quality_gaps'));
        setLoading(false);
    }, [
        patientId,
        patientView,
        servicesContext.patient,
        servicesContext.api,
        PCP,
        clinic,
        searchValues,
        isResultsPage,
        filters,
        buildStats,
        servicesContext.user,
        sortOldestSubmissionAnswers,
        reportYear,
    ]);

    useServiceSubscribe(evt, loadStats);

    React.useEffect(() => {
        // use category cache if enabled, available, and no clinic/PCP filters
        if (
            cacheEnabled &&
            categoryCache.current[reportYear] &&
            prepareSearchQueryString({ clinic, PCP, disenrolled: searchValues.disenrolled }) === ''
        ) {
            setCategories(categoryCache.current[reportYear]);
        } else {
            loadStats();
        }
    }, [reportYear, loadStats, cacheEnabled, clinic, PCP, searchValues.disenrolled]);

    // update the category cache when appropriate
    React.useEffect(() => {
        if (categories.queryString === '' && categories.reportYear && cacheEnabled) {
            categoryCache.current = JSON.parse(
                JSON.stringify({
                    ...categoryCache.current,
                    [categories.reportYear]: categories,
                })
            );
        }
    }, [categories, cacheEnabled]);

    function displayLink(section: StatisticSectionKey, item: StatisticItemKey) {
        if (item === 'percent') {
            return (
                <span
                    className={`badge rounded-pill non-link bg-${
                        (categories[section] as any)[item].class
                    }`}
                >
                    {loading ? '--' : (categories[section] as any)[item].value}
                </span>
            );
        }
        if (item === 'healthhx') {
            return (
                <button
                    type="button"
                    className={`badge rounded-pill bg-${(categories[section] as any)[item].class}`}
                    onClick={() => scrollToItem(item)}
                    aria-label={item}
                >
                    {healthHxVal === 'pending' ? (
                        <Spinner animation="border" size="sm" role="status" variant="secondary">
                            <span className="sr-only">Loading Count</span>
                        </Spinner>
                    ) : (
                        formatNumber(healthHxVal)
                    )}
                </button>
            );
        }
        return (
            <Link
                to="/patients"
                title={(categories[section] as any)[item].title}
                onClick={(e) => getLink(e, section, item)}
                className={`badge rounded-pill ${getActiveBadge(section, item)}${
                    (categories[section] as any)[item].class
                }`}
            >
                {loading && section === 'members' && (
                    <Spinner animation="border" size="sm" role="status" variant="secondary">
                        <span className="sr-only">Loading Count</span>
                    </Spinner>
                )}
                {loading && section !== 'members' && '--'}
                {!loading && formatNumber((categories[section] as any)[item].value)}
            </Link>
        );
    }

    function displayBadge(section: StatisticSectionKey, item: StatisticItemKey) {
        return (
            <div className="badge-col col mb-2 mb-lg-0">
                <span className="d-none d-lg-inline">
                    {(categories[section] as any)[item].label}
                    <br />
                </span>
                {displayLink(section, item)}
            </div>
        );
    }

    return (
        <div className="row mb-4 pb-3 flex-column flex-lg-row text-center" id="statistics">
            <div className="col-12 col-lg-1">
                {!patientView ? (
                    <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                        <div className="col-3 col-lg-12 title text-end text-lg-center">Members</div>
                        {displayBadge('members', 'members')}
                    </div>
                ) : (
                    <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                        <div className="col-3 col-lg-12 title text-end text-lg-center">
                            <span className="d-lg-none">Health Hx</span>
                            <span className="d-lg-inline">&nbsp;</span>
                        </div>
                        {displayBadge('healthhx', 'healthhx')}
                    </div>
                )}
            </div>
            <div className="col-12 col-lg-1">
                {!patientView && (
                    <div className="row badge-row inprogress-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                        <div className="col-3 col-lg-12 title text-end text-lg-center">
                            Work
                            <InProgressAlert timestamp={oldestInProgress} />
                        </div>
                        {displayBadge('gaps', 'in_progress')}
                    </div>
                )}
            </div>
            <div
                className={`col-12 col-lg-4 ${
                    servicesContext.user.hasFeatureToggle('hidden_foc') ? 'offset-lg-2' : ''
                }`}
            >
                <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                    <div className="col-3 col-lg-12 title text-end text-lg-center">HCC/ICD</div>
                    {displayBadge('risk_gaps', enumValue(['gap', 'status', 'Pending']) as number)}
                    {displayBadge('risk_gaps', enumValue(['gap', 'status', 'Fail']) as number)}
                    {displayBadge(
                        'risk_gaps',
                        enumValue(['gap', 'status', 'Under Review']) as number
                    )}
                    {displayBadge('risk_gaps', enumValue(['gap', 'status', 'Addressed']) as number)}
                    {displayBadge('risk_gaps', 'percent')}
                </div>
            </div>
            {!servicesContext.user.hasFeatureToggle('hidden_foc') && (
                <div className="col-12 col-lg-3">
                    <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                        <div className="col-3 col-lg-12 title text-end text-lg-center">AW</div>
                        {displayBadge(
                            'foc_gaps',
                            enumValue(['gap', 'status', 'Pending']) as number
                        )}
                        {displayBadge(
                            'foc_gaps',
                            enumValue(['gap', 'status', 'Under Review']) as number
                        )}
                        {displayBadge(
                            'foc_gaps',
                            enumValue(['gap', 'status', 'Addressed']) as number
                        )}
                    </div>
                </div>
            )}
            <div
                className={`col-12 col-lg-3 ${
                    servicesContext.user.hasFeatureToggle('hidden_foc') ? 'offset-lg-1' : ''
                }`}
            >
                <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                    <div className="col-3 col-lg-12 title text-end text-lg-center">Quality</div>
                    {displayBadge(
                        'quality_gaps',
                        enumValue(['gap', 'status', 'Pending']) as number
                    )}
                    {displayBadge('quality_gaps', enumValue(['gap', 'status', 'Fail']) as number)}
                    {displayBadge(
                        'quality_gaps',
                        enumValue(['gap', 'status', 'Under Review']) as number
                    )}
                    {displayBadge(
                        'quality_gaps',
                        enumValue(['gap', 'status', 'Addressed']) as number
                    )}
                </div>
            </div>
        </div>
    );
}
